import { useEffect, useState } from 'react';
import type {
  ClockConfig,
  HandicapConfig,
  HandicapPiece,
  Ruleset,
  Side,
} from '@xiangqi/protocol';
import { MODE_INFO } from '@xiangqi/protocol';
import { ApiError, api, ensureSession } from '../api.ts';

const PLAYABLE_MODES: Ruleset[] = ['standard', 'blind', 'blind_si_chua', 'blind_tuong_vuong'];

const HANDICAP_PIECES: { id: HandicapPiece; label: string }[] = [
  { id: 'left_chariot', label: 'Xe trái (cột 1)' },
  { id: 'right_chariot', label: 'Xe phải (cột 9)' },
  { id: 'left_horse', label: 'Mã trái (cột 2)' },
  { id: 'right_horse', label: 'Mã phải (cột 8)' },
  { id: 'left_cannon', label: 'Pháo trái (cột 2)' },
  { id: 'right_cannon', label: 'Pháo phải (cột 8)' },
];

const EQUAL_CLOCK_PRESETS: { label: string; initial_ms: number; increment_ms: number }[] = [
  { label: '3 phút + 2s', initial_ms: 3 * 60_000, increment_ms: 2_000 },
  { label: '5 phút + 3s', initial_ms: 5 * 60_000, increment_ms: 3_000 },
  { label: '10 phút + 5s', initial_ms: 10 * 60_000, increment_ms: 5_000 },
  { label: '15 phút + 10s', initial_ms: 15 * 60_000, increment_ms: 10_000 },
  { label: '20 phút + 10s', initial_ms: 20 * 60_000, increment_ms: 10_000 },
];

const TIME_MINUTES_OPTIONS = [1, 2, 3, 5, 8, 10, 15, 20, 30];
const INCREMENT_OPTIONS = [0, 1, 2, 3, 5, 10];

interface CreateRoomModalProps {
  initialRuleset?: Ruleset;
  onClose(): void;
  navigate(path: string): void;
  onStartMatchmaking?(ruleset: Ruleset, clock: ClockConfig, handicap?: HandicapConfig): void;
}

export function CreateRoomModal({
  initialRuleset = 'standard',
  onClose,
  navigate,
  onStartMatchmaking,
}: CreateRoomModalProps) {
  const [ruleset, setRuleset] = useState<Ruleset>(initialRuleset);

  // Đồng hồ & Chấp giờ
  const [timeMode, setTimeMode] = useState<'equal' | 'handicap'>('equal');
  const [selectedEqualIndex, setSelectedEqualIndex] = useState(2); // 10m + 5s
  const [giverMinutes, setGiverMinutes] = useState(5);
  const [receiverMinutes, setReceiverMinutes] = useState(10);
  const [handicapIncrementSeconds, setHandicapIncrementSeconds] = useState(3);

  // Hình thức chấp
  const [handicapType, setHandicapType] = useState<'none' | 'piece' | 'moves' | 'asym_royal'>('none');
  const [giverSide, setGiverSide] = useState<Side>('red');
  const [movesCount, setMovesCount] = useState<2 | 2.5>(2);
  const [firstMoveOption, setFirstMoveOption] = useState<2 | 3>(2);
  const [selectedPieces, setSelectedPieces] = useState<HandicapPiece[]>([]);

  // Bên cầm quân & khán giả
  const [playerSide, setPlayerSide] = useState<Side | null>(null); // null = Ngẫu nhiên
  const [spectatable, setSpectatable] = useState(true);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeBotName, setActiveBotName] = useState(() => {
    try {
      return localStorage.getItem('xq_active_bot_display') || 'AI NNUE';
    } catch {
      return 'AI NNUE';
    }
  });

  useEffect(() => {
    api.getActiveBot()
      .then((res) => {
        if (res.displayName) {
          try {
            localStorage.setItem('xq_active_bot_display', res.displayName);
          } catch {}
          setActiveBotName(res.displayName);
        }
      })
      .catch(() => {});
  }, []);

  const cleanBotLabel = (() => {
    const match = activeBotName.match(/^Bot (?:Kỳ Chiến|Master) \((.+)\)$/);
    if (match) return match[1];
    return activeBotName.replace(/^Bot (?:Kỳ Chiến|Master)\s*/, '');
  })();

  // Khôi phục cấu hình từ localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('xq_saved_config');
      if (!raw) return;
      const saved = JSON.parse(raw);
      // Ưu tiên initialRuleset nếu được truyền vào từ bên ngoài (ví dụ bấm tile Cờ Úp ở Home)
      const targetRuleset: Ruleset = initialRuleset || (saved.ruleset && PLAYABLE_MODES.includes(saved.ruleset) ? saved.ruleset : 'standard');
      setRuleset(targetRuleset);
      if (saved.playerSide !== undefined) setPlayerSide(saved.playerSide);
      if (typeof saved.spectatable === 'boolean') setSpectatable(saved.spectatable);

      if (saved.timeMode) setTimeMode(saved.timeMode);
      if (typeof saved.selectedEqualIndex === 'number') setSelectedEqualIndex(saved.selectedEqualIndex);
      if (saved.giverMinutes) setGiverMinutes(saved.giverMinutes);
      if (saved.receiverMinutes) setReceiverMinutes(saved.receiverMinutes);
      if (typeof saved.handicapIncrementSeconds === 'number') {
        setHandicapIncrementSeconds(saved.handicapIncrementSeconds);
      }

      if (saved.handicap) {
        const loadedType = saved.handicap.type ?? 'none';
        if (targetRuleset === 'blind') {
          // Cờ Úp thường: Tuyệt đối không chấp quân, không chấp nước (đồng cân)
          setHandicapType('none');
          setSelectedPieces([]);
        } else if (targetRuleset === 'standard') {
          if (loadedType === 'piece' || loadedType === 'moves') {
            setHandicapType(loadedType);
          } else {
            setHandicapType('none');
          }
        } else if (targetRuleset === 'blind_si_chua' || targetRuleset === 'blind_tuong_vuong') {
          if (loadedType === 'asym_royal') {
            setHandicapType('asym_royal');
          } else {
            setHandicapType('none');
          }
        } else {
          setHandicapType('none');
        }

        if (saved.handicap.giver_side) setGiverSide(saved.handicap.giver_side);
        if (saved.handicap.moves_count === 2 || saved.handicap.moves_count === 2.5) {
          setMovesCount(saved.handicap.moves_count);
        }
        if (saved.handicap.current_moves === 2 || saved.handicap.current_moves === 3) {
          setFirstMoveOption(saved.handicap.current_moves);
        }
        if (Array.isArray(saved.handicap.pieces)) {
          setSelectedPieces(saved.handicap.pieces);
        }
      } else {
        setHandicapType('none');
      }
    } catch {
      // ignore
    }
  }, [initialRuleset]);

  // Xử lý phím Escape để đóng modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const buildClockConfig = (): ClockConfig => {
    if (timeMode === 'equal') {
      const p = EQUAL_CLOCK_PRESETS[selectedEqualIndex] ?? EQUAL_CLOCK_PRESETS[2]!;
      return { initial_ms: p.initial_ms, increment_ms: p.increment_ms };
    }
    const maxMs = Math.max(giverMinutes, receiverMinutes) * 60_000;
    return {
      initial_ms: maxMs,
      increment_ms: handicapIncrementSeconds * 1_000,
    };
  };

  const buildHandicapConfig = (): HandicapConfig | undefined => {
    const hasTimeHandicap = timeMode === 'handicap' && giverMinutes !== receiverMinutes;
    const timeObj = hasTimeHandicap
      ? {
          giver_ms: giverMinutes * 60_000,
          receiver_ms: receiverMinutes * 60_000,
        }
      : undefined;

    // Cờ Úp thường: Tuyệt đối không chấp quân, không chấp nước đi trước (đồng cân)
    if (ruleset === 'blind') {
      if (hasTimeHandicap) {
        return {
          type: 'none',
          giver_side: giverSide,
          pieces: [],
          moves_count: 2,
          time: timeObj,
        };
      }
      return undefined;
    }

    if (handicapType === 'none') {
      if (hasTimeHandicap) {
        return {
          type: 'none',
          giver_side: giverSide,
          pieces: [],
          moves_count: 2,
          time: timeObj,
        };
      }
      return undefined;
    }

    if (handicapType === 'piece' && ruleset === 'standard') {
      if (selectedPieces.length === 0 && !hasTimeHandicap) return undefined;
      return {
        type: selectedPieces.length > 0 ? 'piece' : 'none',
        giver_side: giverSide,
        pieces: selectedPieces,
        moves_count: 2,
        ...(timeObj ? { time: timeObj } : {}),
      };
    }

    if (handicapType === 'moves' && ruleset === 'standard') {
      return {
        type: 'moves',
        giver_side: giverSide,
        pieces: [],
        moves_count: movesCount,
        ...(movesCount === 2.5 ? { current_moves: firstMoveOption } : { current_moves: 2 }),
        ...(timeObj ? { time: timeObj } : {}),
      };
    }

    if (handicapType === 'asym_royal' && (ruleset === 'blind_si_chua' || ruleset === 'blind_tuong_vuong')) {
      const moves = ruleset === 'blind_si_chua' ? 7 : 6;
      return {
        type: 'asym_royal',
        giver_side: giverSide,
        pieces: [],
        moves_count: moves,
        current_moves: moves,
        ...(timeObj ? { time: timeObj } : {}),
      };
    }

    return undefined;
  };

  const saveConfig = (handicap?: HandicapConfig) => {
    try {
      localStorage.setItem(
        'xq_saved_config',
        JSON.stringify({
          ruleset,
          timeMode,
          selectedEqualIndex,
          giverMinutes,
          receiverMinutes,
          handicapIncrementSeconds,
          playerSide,
          spectatable,
          handicap: handicap ?? {
            type: 'none',
            giver_side: giverSide,
            pieces: [],
            moves_count: 2,
          },
        }),
      );
    } catch {
      // ignore
    }
  };

  const handleCreateRoom = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await ensureSession();
      const clock = buildClockConfig();
      const handicap = buildHandicapConfig();
      saveConfig(handicap);

      const res = await api.createRoom(ruleset, clock, {
        ...(playerSide ? { host_side: playerSide } : {}),
        spectatable,
        ...(handicap ? { handicap } : {}),
      });

      navigate(`/r/${res.room.room_code}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Không tạo được phòng');
      setBusy(false);
    }
  };

  const handlePlayWithBot = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await ensureSession();
      const clock = buildClockConfig();
      const handicap = buildHandicapConfig();
      saveConfig(handicap);

      const res = await api.createRoom(ruleset, clock, {
        vs_bot: {
          profile: 'master',
          human_side: playerSide,
        },
        spectatable,
        ...(handicap ? { handicap } : {}),
      });

      navigate(`/r/${res.room.room_code}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Không tạo được trận với Bot');
      setBusy(false);
    }
  };

  const handleStartQuickMatch = () => {
    const clock = buildClockConfig();
    const handicap = buildHandicapConfig();
    saveConfig(handicap);
    onClose();
    if (onStartMatchmaking) {
      onStartMatchmaking(ruleset, clock, handicap);
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal create-room-modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <div className="modal-title-row">
            <h2>⚔️ Tùy chọn trận đấu</h2>
            <button className="close-btn" onClick={onClose} aria-label="Đóng">
              ✕
            </button>
          </div>
        </div>

        <div className="modal-body">
          {/* 1. THỂ THỨC CHƠI */}
          <div className="config-section">
            <label className="section-label">1. Thể thức cờ</label>
            <div className="ruleset-grid">
              {PLAYABLE_MODES.map((m) => {
                const info = MODE_INFO[m];
                const active = ruleset === m;
                return (
                  <button
                    key={m}
                    type="button"
                    className={`ruleset-card ${active ? 'selected' : ''}`}
                    onClick={() => {
                      setRuleset(m);
                      if (m === 'blind') {
                        setHandicapType('none');
                        setSelectedPieces([]);
                      } else if (m === 'standard') {
                        if (handicapType === 'asym_royal') setHandicapType('none');
                      } else if (m === 'blind_si_chua' || m === 'blind_tuong_vuong') {
                        if (handicapType === 'piece' || handicapType === 'moves') setHandicapType('none');
                      }
                    }}
                  >
                    <span className="card-name">{info.label}</span>
                    <span className="card-desc">
                      {m === 'standard' && 'Truyền thống'}
                      {m === 'blind' && 'Quân úp bí mật'}
                      {m === 'blind_si_chua' && 'Sĩ Chúa lộng hành'}
                      {m === 'blind_tuong_vuong' && 'Tượng Vương qua sông'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* CÁCH CHƠI BIẾN THỂ SĨ CHÚA / TƯỢNG VƯƠNG */}
          {(ruleset === 'blind_si_chua' || ruleset === 'blind_tuong_vuong') && (
            <div className="config-section">
              <label className="section-label">
                Cách chơi {ruleset === 'blind_si_chua' ? 'Sĩ Chúa' : 'Tượng Vương'}
              </label>
              <div className="ruleset-grid">
                <button
                  type="button"
                  className={`ruleset-card ${handicapType !== 'asym_royal' ? 'selected' : ''}`}
                  onClick={() => setHandicapType('none')}
                >
                  <span className="card-name">
                    {ruleset === 'blind_si_chua' ? 'Sĩ Chúa vs Sĩ Chúa' : 'Tượng Vương vs Tượng Vương'}
                  </span>
                  <span className="card-desc">
                    Đồng thể thức. Ván 1 ngẫu nhiên, tái đấu đi tiên lần lượt.
                  </span>
                </button>
                <button
                  type="button"
                  className={`ruleset-card ${handicapType === 'asym_royal' ? 'selected' : ''}`}
                  onClick={() => setHandicapType('asym_royal')}
                >
                  <span className="card-name">
                    Đấu Cờ Úp thường (Chấp {ruleset === 'blind_si_chua' ? '7' : '6'} nước)
                  </span>
                  <span className="card-desc">
                    Bên Cờ Úp thường được đi trước {ruleset === 'blind_si_chua' ? '7' : '6'} nước liên tiếp trên sân nhà.
                  </span>
                </button>
              </div>

              {handicapType === 'asym_royal' && (
                <div className="handicap-subpanel" style={{ marginTop: 8 }}>
                  <div className="side-selector-row">
                    <span className="picker-label">Bên cầm {ruleset === 'blind_si_chua' ? 'Sĩ Chúa' : 'Tượng Vương'}:</span>
                    <div className="segmented-control mini">
                      <button
                        type="button"
                        className={giverSide === 'red' ? 'active' : ''}
                        onClick={() => setGiverSide('red')}
                      >
                        Đỏ
                      </button>
                      <button
                        type="button"
                        className={giverSide === 'black' ? 'active' : ''}
                        onClick={() => setGiverSide('black')}
                      >
                        Đen
                      </button>
                    </div>
                  </div>
                  <p className="hint">
                    💡 Bên Cờ Úp thường ({giverSide === 'red' ? 'Đen' : 'Đỏ'}) được đi trước {ruleset === 'blind_si_chua' ? '7' : '6'} nước liên tiếp trên sân nhà (không qua sông, không ăn quân, không chiếu).
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 2. THỜI GIAN & CHẤP GIỜ */}
          <div className="config-section">
            <div className="section-header-row">
              <label className="section-label">2. Thời gian & Chấp giờ</label>
              <div className="segmented-control mini">
                <button
                  type="button"
                  className={timeMode === 'equal' ? 'active' : ''}
                  onClick={() => setTimeMode('equal')}
                >
                  ⏱️ Đều nhau
                </button>
                <button
                  type="button"
                  className={timeMode === 'handicap' ? 'active' : ''}
                  onClick={() => setTimeMode('handicap')}
                >
                  ⚡ Chấp giờ
                </button>
              </div>
            </div>

            {timeMode === 'equal' ? (
              <div className="pills-row">
                {EQUAL_CLOCK_PRESETS.map((preset, idx) => (
                  <button
                    key={preset.label}
                    type="button"
                    className={`pill-btn ${selectedEqualIndex === idx ? 'selected' : ''}`}
                    onClick={() => setSelectedEqualIndex(idx)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="handicap-time-box">
                <div className="picker-grid">
                  <div className="picker-col">
                    <span className="picker-label">Bên chấp ({giverSide === 'red' ? 'Đỏ' : 'Đen'}):</span>
                    <select
                      value={giverMinutes}
                      onChange={(e) => setGiverMinutes(Number(e.target.value))}
                      className="picker-select"
                    >
                      {TIME_MINUTES_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {m} phút
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="picker-col">
                    <span className="picker-label">Bên nhận ({giverSide === 'red' ? 'Đen' : 'Đỏ'}):</span>
                    <select
                      value={receiverMinutes}
                      onChange={(e) => setReceiverMinutes(Number(e.target.value))}
                      className="picker-select"
                    >
                      {TIME_MINUTES_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {m} phút
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="picker-col">
                    <span className="picker-label">Cộng giây mỗi nước:</span>
                    <select
                      value={handicapIncrementSeconds}
                      onChange={(e) => setHandicapIncrementSeconds(Number(e.target.value))}
                      className="picker-select"
                    >
                      {INCREMENT_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          +{s} giây
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 3. HÌNH THỨC CHẤP TRONG CỜ TRUYỀN THỐNG / CỜ ÚP */}
          {ruleset === 'standard' && (
            <div className="config-section">
              <label className="section-label">3. Chấp quân & Chấp tiên</label>
              <div className="pills-row">
                <button
                  type="button"
                  className={`pill-btn ${handicapType === 'none' ? 'selected' : ''}`}
                  onClick={() => setHandicapType('none')}
                >
                  Đồng cân (Không chấp)
                </button>
                <button
                  type="button"
                  className={`pill-btn ${handicapType === 'piece' ? 'selected' : ''}`}
                  onClick={() => setHandicapType('piece')}
                >
                  Chấp quân
                </button>
                <button
                  type="button"
                  className={`pill-btn ${handicapType === 'moves' ? 'selected' : ''}`}
                  onClick={() => setHandicapType('moves')}
                >
                  Chấp tiên
                </button>
              </div>

              {(handicapType !== 'none' || timeMode === 'handicap') && (
                <div className="handicap-subpanel">
                  <div className="side-selector-row">
                    <span className="picker-label">Bên chấp:</span>
                    <div className="segmented-control mini">
                      <button
                        type="button"
                        className={giverSide === 'red' ? 'active' : ''}
                        onClick={() => setGiverSide('red')}
                      >
                        Đỏ chấp
                      </button>
                      <button
                        type="button"
                        className={giverSide === 'black' ? 'active' : ''}
                        onClick={() => setGiverSide('black')}
                      >
                        Đen chấp
                      </button>
                    </div>
                  </div>

                  {handicapType === 'piece' && (
                    <div className="pieces-checkbox-grid">
                      <span className="picker-label full-width">Chọn quân chấp:</span>
                      {HANDICAP_PIECES.map((p) => {
                        const sel = selectedPieces.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            className={`piece-toggle-btn ${sel ? 'active' : ''}`}
                            onClick={() => {
                              if (sel) {
                                setSelectedPieces(selectedPieces.filter((x) => x !== p.id));
                              } else {
                                setSelectedPieces([...selectedPieces, p.id]);
                              }
                            }}
                          >
                            {sel ? '✓ ' : ''}
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {handicapType === 'moves' && (
                    <div className="moves-handicap-row">
                      <span className="picker-label">Số nước chấp:</span>
                      <div className="pills-row mini">
                        <button
                          type="button"
                          className={`pill-btn mini ${movesCount === 2 ? 'selected' : ''}`}
                          onClick={() => setMovesCount(2)}
                        >
                          2 nước đi trước
                        </button>
                        <button
                          type="button"
                          className={`pill-btn mini ${movesCount === 2.5 ? 'selected' : ''}`}
                          onClick={() => setMovesCount(2.5)}
                        >
                          2,5 nước (luân phiên 2 & 3 nước)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 4. CÀI ĐẶT NGƯỜI CHƠI */}
          <div className="config-section">
            <label className="section-label">4. Cài đặt người chơi</label>
            <div className="player-settings-grid">
              <div className="setting-item">
                <span className="picker-label">Bạn cầm quân:</span>
                <div className="pills-row mini">
                  {(
                    [
                      ['red', 'Cầm Đỏ'],
                      ['black', 'Cầm Đen'],
                      [null, 'Ngẫu nhiên'],
                    ] as const
                  ).map(([val, lbl]) => (
                    <button
                      key={lbl}
                      type="button"
                      className={`pill-btn mini ${playerSide === val ? 'selected' : ''}`}
                      onClick={() => setPlayerSide(val)}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              <div className="setting-item checkbox-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={spectatable}
                    onChange={(e) => setSpectatable(e.target.checked)}
                  />
                  Cho phép khán giả vào xem trận đấu
                </label>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Đóng
          </button>
          {onStartMatchmaking && (
            <button
              type="button"
              className="btn-primary"
              style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' }}
              onClick={handleStartQuickMatch}
              disabled={busy}
            >
              ⚔ Tìm trận nhanh
            </button>
          )}
          <button
            type="button"
            className="btn-primary"
            style={{ background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' }}
            onClick={() => void handlePlayWithBot()}
            disabled={busy}
            data-testid="submit-play-with-bot"
          >
            {busy ? 'Đang khởi tạo…' : `🤖 Đấu với Bot (${cleanBotLabel})`}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handleCreateRoom()}
            disabled={busy}
            data-testid="submit-create-room"
          >
            {busy ? 'Đang khởi tạo…' : '＋ Tạo phòng mời bạn'}
          </button>
        </div>
      </div>
    </div>
  );
}
